import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import BadgeSquare from 'components/badge/badgeSquare';
import { ChevronLeft, ChevronRight, Dots } from 'assets/icons';
import ApiDatabase from 'server';
import { toast } from 'react-toastify';

const STATUS_BADGE_MAP = {
  paid: 14,
  pending: 20,
  partially_paid: 16,
  late: 17,
  cancelled: 18,
  draft: 19,
  to_import_on_pennylane: 21,
};

const STATUS_LABEL_MAP = {
  paid: 'Payé',
  pending: 'En attente',
  partially_paid: 'Partiellement payé',
  late: 'En retard',
  cancelled: 'Annulé',
  draft: 'Brouillon',
  to_import_on_pennylane: 'À importer sur PennyLane',
};

const resolveStatus = (item) => {
  if (item.payment_status && STATUS_BADGE_MAP[item.payment_status] !== undefined) return item.payment_status;
  if (typeof item.status === 'boolean') return item.status ? 'paid' : 'pending';
  return 'pending';
};

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const InvoiceCompany = ({ companyInfo, initialYear }) => {
  const [selectedYear, setSelectedYear] = useState(
    initialYear || new Date().getFullYear()
  );

  const factures = companyInfo?.factures || [];

  const processed = useMemo(
    () =>
      factures
        .map((f) => {
          const d = new Date(f.date_facture);
          return {
            ...f,
            _year: isNaN(d) ? null : d.getFullYear(),
            _month: isNaN(d) ? null : d.getMonth() + 1,
          };
        })
        .filter((f) => f._year === selectedYear)
        .sort((a, b) => b._month - a._month || (b.external_id || 0) - (a.external_id || 0)),
    [factures, selectedYear]
  );

  const yearTotal = useMemo(
    () => processed.reduce((sum, f) => sum + (Number(f.amount) || 0), 0),
    [processed]
  );

  return (
    <div className="border rounded p-3 flex flex-col gap-4">
      <div className="flex justify-between gap-2 items-center">
        <div className="font-medium">Factures</div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-1 text-sm">
            <button
              className="flex items-center justify-center w-8 h-8 rounded-lg border hover:bg-gray-100"
              onClick={() => setSelectedYear((y) => y - 1)}
            >
              <ChevronLeft wh={14} color="#374151" />
            </button>
            <div className="min-w-[60px] h-8 flex items-center justify-center border rounded-lg text-center text-sm font-medium">
              {selectedYear}
            </div>
            <button
              className="flex items-center justify-center w-8 h-8 rounded-lg border hover:bg-gray-100"
              onClick={() => setSelectedYear((y) => y + 1)}
            >
              <ChevronRight wh={14} color="#374151" />
            </button>
          </div>
          <div className="border rounded py-1 px-3 text-sm font-medium bg-gray-50">
            {yearTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
          </div>
        </div>
      </div>

      {processed.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-4">
          Aucune facture pour cette année
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-2 py-2">Période</th>
                <th className="px-2 py-2">N°</th>
                <th className="px-2 py-2">Montant</th>
                <th className="px-2 py-2">Paiement</th>
                <th className="px-2 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {processed.map((item) => (
                <InvoiceCompanyRow
                  key={item.external_id || item._id}
                  item={item}
                  companyInfo={companyInfo}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const InvoiceCompanyRow = ({ item, companyInfo }) => {
  const { toggle } = useModal();
  const {
    setAdminType,
    setAdminTempWorkerId,
    setAdminUserId,
    setAdminFileName,
    setModalData,
  } = UseContext();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropDownRef = useRef(null);
  const dotsRef = useRef(null);

  const updateDropdownPos = useCallback(() => {
    if (dotsRef.current) {
      const rect = dotsRef.current.getBoundingClientRect();
      const menuHeight = 120;
      const fitsBelow = rect.bottom + 4 + menuHeight < window.innerHeight;
      setDropdownPos({
        top: fitsBelow ? rect.bottom + 4 : rect.top - menuHeight - 4,
        left: rect.right - 176,
      });
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        dropDownRef.current && !dropDownRef.current.contains(e.target) &&
        dotsRef.current && !dotsRef.current.contains(e.target)
      )
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const invoiceStatus = resolveStatus(item);

  const enriched = useMemo(() => ({
    invoice_number: item.external_id,
    id_company_bestt: companyInfo?.id_bestt,
    id_company: companyInfo?._id,
    amount: item.amount,
    date_facture: item.date_facture,
    status: 'imported',
    paid_status: invoiceStatus,
  }), [item, companyInfo, invoiceStatus]);

  const year = String(item._year);
  const month = String(item._month).padStart(2, '0');

  const handleViewInvoice = () => {
    setDropdownOpen(false);
    const fileName = `invoice_${companyInfo?.id_bestt}_${item.external_id}`;
    setAdminType('invoice');
    setAdminTempWorkerId('');
    setAdminUserId(companyInfo?._id);
    setAdminFileName(fileName);
    toggle('ad_file_view');
  };

  const handleDelete = () => {
    setDropdownOpen(false);
    setModalData({ invoices: [enriched], year, month });
    toggle('invoice_delete');
  };

  const [pushingPL, setPushingPL] = useState(false);
  const handlePushPennyLane = () => {
    setDropdownOpen(false);
    const token = localStorage.getItem('xsrfToken');
    setPushingPL(true);
    ApiDatabase.pushInvoicesToPennyLane(
      { token, invoice_numbers: [item.external_id] },
      (data) => {
        setPushingPL(false);
        if (data.created > 0) toast.success(`Facture ${item.external_id} envoyée sur PennyLane`);
        if (data.failed > 0) toast.warning(`Erreur pour la facture ${item.external_id}`);
      },
      (err) => {
        setPushingPL(false);
        toast.error(err.response?.data?.error || 'Erreur lors de l\'envoi vers PennyLane');
      }
    );
  };

  const monthLabel = MONTH_NAMES[item._month - 1] || '';

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-2">
        {monthLabel} {year}
      </td>
      <td className="px-2 py-2">
        {item.external_id || '-'}
      </td>
      <td className="px-2 py-2">
        {item.amount
          ? `${Number(item.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
          : '-'}
      </td>
      <td className="px-2 py-2">
        <BadgeSquare type={STATUS_BADGE_MAP[invoiceStatus] ?? 20} />
      </td>
      <td className="px-2 py-2">
        <div
          className="cursor-pointer w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100"
          ref={dotsRef}
          onClick={() => {
            updateDropdownPos();
            setDropdownOpen(!dropdownOpen);
          }}
        >
          <Dots w={14} h={4} color="#374151" />
        </div>
        {dropdownOpen && createPortal(
          <div
            ref={dropDownRef}
            className="fixed border-2 border-[#F3F4F6] rounded-lg shadow-lg w-44 bg-gray-50 z-[9999]"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            <div
              className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-gray-900"
              onClick={handleViewInvoice}
            >
              Consulter la facture
            </div>
            <div className="border-b border-gray-100" />
            <div
              className={`flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 ${pushingPL ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={handlePushPennyLane}
            >
              {pushingPL
                ? 'Envoi en cours...'
                : invoiceStatus && invoiceStatus !== 'to_import_on_pennylane'
                  ? 'Réimporter sur PennyLane'
                  : 'Exporter vers PennyLane'}
            </div>
            <div className="border-b border-gray-100" />
            <div
              className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-red-500"
              onClick={handleDelete}
            >
              Supprimer
            </div>
          </div>,
          document.body
        )}
      </td>
    </tr>
  );
};

export default InvoiceCompany;
